import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Stack from '@mui/material/Stack'
import { OnboardingStep, TwinInstruction } from 'api/model'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useState } from 'react'
import AddOrEditInstructionModal from './AddOrEditInstructionModal'
import { toastAtom } from 'state'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useFetchInstructions } from 'hooks/use-instructions'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { useActiveOrganization } from 'hooks/use-organization'
import { twinsDeleteInstruction } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { getColorForDarkMode } from 'utils'
import FtuxTooltip from 'components/FtuxTooltip'
import { useNavigate } from 'react-router-dom'
import EmptyStateSection from './EmptyStateSection'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import { useFtuxTooltip } from 'hooks/use-ftux'
import { myTwinAtom } from 'state/my-twin'
import { COLORS } from 'app-constants'

const InstructionRow = ({
  instruction,
  onEditInstruction,
  onDeleteInstruction,
}: {
  instruction: TwinInstruction
  onEditInstruction: (instruction: TwinInstruction) => void
  onDeleteInstruction: (instructionId: string) => void
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const onMouseEnter = () => setIsHovered(true)
  const onMouseLeave = () => setIsHovered(false)

  return (
    <Stack
      px={2}
      py={3}
      height="32px"
      direction="row"
      alignItems="center"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        '&:hover': {
          backgroundColor: COLORS.GRAY_8,
          borderRadius: '8px',
        },
      }}>
      <Stack direction="row" alignItems="center" width="100%">
        <Tooltip title={instruction.query || 'No topic provided'}>
          <Typography
            variant="pS"
            flex="1 0 300px"
            maxWidth="300px"
            mr={3}
            sx={{
              color: !instruction.query ? COLORS.GRAY_8 : COLORS.GRAY_0,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}>
            {instruction.query || 'No topic provided'}
          </Typography>
        </Tooltip>

        <Tooltip title={instruction.instructions || 'No instructions provided'}>
          <Typography
            variant="pS"
            sx={{
              color: !instruction.instructions ? COLORS.GRAY_8 : COLORS.GRAY_0,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}>
            {instruction.instructions || 'No instructions provided'}
          </Typography>
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          visibility: isHovered ? 'visible' : 'hidden',
        }}>
        <IconButton
          onClick={() => {
            onEditInstruction(instruction)
          }}>
          <EditOutlinedIcon
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
        </IconButton>

        <IconButton
          onClick={() => {
            onDeleteInstruction(instruction.id)
          }}>
          <DeleteOutlinedIcon
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
        </IconButton>
      </Stack>
    </Stack>
  )
}

const InstructionsList = ({
  instructions,
  onEditInstruction,
  onDeleteInstruction,
}: {
  instructions: TwinInstruction[]
  onEditInstruction: (instruction: TwinInstruction) => void
  onDeleteInstruction: (instructionId: string) => void
}) => {
  if (instructions.length === 0) {
    return <EmptyStateSection headerText="No instructions yet" />
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" width="100%">
        <Typography flex="1 0 300px" maxWidth="300px" pl={2} mr={5} variant="h6">
          Topic
        </Typography>

        <Typography variant="h6">Instruction</Typography>
      </Stack>
      {instructions.map((instruction) => (
        <InstructionRow
          key={instruction.id}
          instruction={instruction}
          onEditInstruction={onEditInstruction}
          onDeleteInstruction={onDeleteInstruction}
        />
      ))}
    </Stack>
  )
}

const MyTwinInstructions = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()
  const setToast = useSetRecoilState(toastAtom)
  const theme = useTheme()

  const myTwin = useRecoilValue(myTwinAtom)

  const { instructions, isLoading: isLoadingInstructions, setInstructions } = useFetchInstructions(myTwin?.id ?? '')

  const [isAddInstructionModalOpen, setIsAddInstructionModalOpen] = useState(false)
  const [editedInstruction, setEditedInstruction] = useState<TwinInstruction | null>(null)

  const [isDeleteInstructionDialogOpen, setIsDeleteInstructionDialogOpen] = useState(false)
  const [pendingDeleteInstructionId, setPendingDeleteInstructionId] = useState<string>('')

  const [isDeletingInstruction, setIsDeletingInstruction] = useState(false)

  const { isFtuxTooltipOpen, setIsFtuxTooltipOpen } = useFtuxTooltip()

  const onEditInstruction = (instruction: TwinInstruction) => {
    setEditedInstruction(instruction)
    setIsAddInstructionModalOpen(true)
  }

  const onDeleteInstruction = (instructionId: string) => {
    setIsDeleteInstructionDialogOpen(true)
    setPendingDeleteInstructionId(instructionId)
  }

  const onAddInstruction = (instruction: TwinInstruction) => {
    setInstructions([...instructions, instruction])
  }

  const onUpdateInstruction = (instruction: TwinInstruction) => {
    setInstructions(instructions.map((i) => (i.id === instruction.id ? instruction : i)))
  }

  const onConfirmDeleteInstruction = async () => {
    setIsDeletingInstruction(true)

    if (!activeOrganization) {
      setIsDeletingInstruction(false)
      return
    }

    try {
      await twinsDeleteInstruction(activeOrganization, myTwin?.id || '', pendingDeleteInstructionId)
      setToast({
        severity: 'success',
        title: 'Successfully deleted instruction',
      })
      setInstructions(instructions.filter((i) => i.id !== pendingDeleteInstructionId))
      setPendingDeleteInstructionId('')
      setIsDeleteInstructionDialogOpen(false)
    } catch (error) {
      setToast({
        severity: 'error',
        title: 'Failed to delete instruction',
      })
    } finally {
      setIsDeletingInstruction(false)
    }
  }

  const renderContent = () => {
    if (isLoadingInstructions) {
      return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
    }
    return (
      <InstructionsList
        instructions={instructions}
        onEditInstruction={onEditInstruction}
        onDeleteInstruction={onDeleteInstruction}
      />
    )
  }

  return (
    <Stack
      borderLeft={`1px solid ${COLORS.GRAY_5}`}
      width="100%"
      height="100vh"
      p={3}
      sx={{
        backgroundColor: COLORS.GRAY_8,
      }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        maxWidth="1024px">
        <Stack pr={8}>
          {!isMobile && (
            <Typography variant="h4" mb={2}>
              Instructions
            </Typography>
          )}

          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Specific data sources for your twin to reference when answering questions with different topics. This will
            narrow down the context that your twin has to search through to answer questions.
          </Typography>
        </Stack>
      </Stack>

      <FtuxTooltip
        open={isMobile ? false : isFtuxTooltipOpen} // we use a mobile specific ftux tooltip
        setOpen={setIsFtuxTooltipOpen}
        headerText="Add instruction"
        subtitleText="Instruct your twin to use specific data sources when answering questions about different topics."
        progressText="6 of 6"
        onBackClick={() => {
          navigate('/my-twin/files')
        }}
        onNextClick={updateOnboardingStep}
        isLastStep
        placement="right-start"
        onDismiss={() => {
          setIsFtuxTooltipOpen(false)
        }}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            setEditedInstruction(null)
            setIsAddInstructionModalOpen(true)
          }}
          startIcon={<AddOutlinedIcon />}
          sx={{
            maxHeight: '36px',
            width: 'fit-content',
            mb: 8,
          }}>
          Add instruction
        </Button>
      </FtuxTooltip>

      {renderContent()}

      {isAddInstructionModalOpen && (
        <AddOrEditInstructionModal
          instruction={editedInstruction}
          onAddInstruction={onAddInstruction}
          onUpdateInstruction={onUpdateInstruction}
          twinId={myTwin?.id ?? ''}
          onClose={() => setIsAddInstructionModalOpen(false)}
        />
      )}

      <Dialog open={isDeleteInstructionDialogOpen}>
        <DialogTitle>Are you sure you want to delete this instruction?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteInstructionDialogOpen(false)
              setPendingDeleteInstructionId('')
            }}
            disabled={isDeletingInstruction}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onConfirmDeleteInstruction}
            disabled={isDeletingInstruction}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default withOnboardingStep(MyTwinInstructions, OnboardingStep.twin_ftux_tour_completed)
